import {
  CustomerInfo,
  Entitlement,
  IntroPrice,
  SubscriptionPackage,
  SubscriptionProduct
} from './purchase-provider.interface';
import { mapEffectiveOffer } from './effective-offer';

/**
 * Maps RevenueCat Capacitor SDK objects to our
 * PurchaseProvider interface types.
 *
 * The SDK types live in a transitive dependency
 * (@revenuecat/purchases-typescript-internal-esm)
 * so we accept `any` and return fully-typed objects.
 * Each method documents the expected SDK fields.
 */
export class CapacitorSdkMapper {

  mapIntroPrice(sdkIntro: any): IntroPrice {
    return {
      price: sdkIntro.price,
      priceString: sdkIntro.priceString,
      period: sdkIntro.period ?? null
    };
  }

  mapProduct(sdkProduct: any): SubscriptionProduct {
    const product: SubscriptionProduct = {
      identifier: sdkProduct.identifier,
      title: sdkProduct.title,
      price: sdkProduct.price,
      priceString: sdkProduct.priceString,
      currencyCode: sdkProduct.currencyCode,
      productType: sdkProduct.productType,
      subscriptionPeriod:
        sdkProduct.subscriptionPeriod ?? null,
      pricePerMonthString:
        sdkProduct.pricePerMonthString ?? null,
      introPrice: sdkProduct.introPrice
        ? this.mapIntroPrice(sdkProduct.introPrice)
        : null,
      defaultOption:
        sdkProduct.defaultOption ?? null,
      offeringIdentifier:
        sdkProduct.presentedOfferingContext
          ?.offeringIdentifier ?? null
    };
    // Shape-derived default; on iOS,
    // CapacitorPurchaseProvider replaces this once
    // eligibility/signing is known.
    product.effectiveOffer = mapEffectiveOffer(product);
    return product;
  }

  mapPackage(sdkPkg: any): SubscriptionPackage {
    return {
      identifier: sdkPkg.identifier,
      product: this.mapProduct(sdkPkg.product)
    };
  }

  mapEntitlement(sdkEnt: any): Entitlement {
    return {
      identifier: sdkEnt.identifier,
      isActive: sdkEnt.isActive,
      willRenew: sdkEnt.willRenew,
      productIdentifier: sdkEnt.productIdentifier,
      latestPurchaseDate:
        sdkEnt.latestPurchaseDate,
      expirationDate:
        sdkEnt.expirationDate ?? null,
      // Already uppercase on the Capacitor SDK
      periodType: sdkEnt.periodType,
      // Android/Play only; undefined/null on iOS
      productPlanIdentifier:
        sdkEnt.productPlanIdentifier ?? null
    };
  }

  mapEntitlementRecord(
    record: Record<string, any> | null | undefined
  ): Record<string, Entitlement> {
    if (!record) return {};
    const result: Record<string, Entitlement> = {};
    for (const [key, val] of Object.entries(
      record
    )) {
      result[key] = this.mapEntitlement(val);
    }
    return result;
  }

  mapCustomerInfo(sdkInfo: any): CustomerInfo {
    return {
      entitlements: {
        active: this.mapEntitlementRecord(
          sdkInfo.entitlements.active
        ),
        all: this.mapEntitlementRecord(
          sdkInfo.entitlements.all
        )
      },
      originalPurchaseDate:
        sdkInfo.originalPurchaseDate ?? null,
      latestExpirationDate:
        sdkInfo.latestExpirationDate ?? null
    };
  }
}
